import React, { useState } from "react";
import * as XLSX from "xlsx";
import { Card, CardBody, CardHeader, Divider, Spinner, Button, Input, CardFooter, TableRow, TableCell, Select, SelectItem } from "@nextui-org/react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import toast from "react-hot-toast";
import { postFetcher } from "../../../../services/api";
import { ArrowDownTrayIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ClientTablePagination from "../../../../components/elements/ClientTablePagination";
import TipeSurat from "../../../../enums/tipeSurat";
import ControlledInput from "../../../../components/elements/ControlledInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { filterValidRows } from "../../../../services/helpers";
import useBadRequestForm from "../../../../hooks/useBadRequestForm";
import useListCompany from "../../../../hooks/useListCompany";
import ControlledReactSelect from "../../../../components/elements/ControlledReactSelect";
import useAuth from "../../../../hooks/useAuth";
import { addManualRecomendationValidation } from "../../../../services/validation/recomendation.validation";

export default function UploadExcelPage() {
  const fileUrl = "/files/Draft_Form_Tambah_Kendaraan.xlsx";
  const navigate = useNavigate();
  const { isSuperAdmin, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { formErrorMessage, setFormErrorMessage, isResponseErrorObject } = useBadRequestForm();
  const { dataCompany, isLoadingCompany } = useListCompany();
  const [isUploaded, setIsUploaded] = useState(false);
  const [vehicleData, setVehicleData] = useState([]);
  const [b3Data, setB3Data] = useState([]);
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({ resolver: yupResolver(addManualRecomendationValidation) });
  const type = watch("tipe");

  const REQUIRED_FIELDS_VEHICLE = ["No", "No_Polisi", "Merek", "Tahun", "No_Rangka", "No_Mesin", "Kepemilikan"];
  const formattedHeaderTableVehicle = REQUIRED_FIELDS_VEHICLE.map((header) => header.replace(/_/g, " "));
  const REQUIRED_FIELDS_B3 = ["No", "Nama_Dagang", "Cas_Number", "Karakteristik", "Fasa_B3", "Jenis_Kemasan", "Tujuan_Penggunaan"];
  const formattedHeaderTableMaterial = REQUIRED_FIELDS_B3.map((header) => header.replace(/_/g, " "));

  const contentVehicle = (item) => (
    <TableRow key={item.id}>
      <TableCell>{item.No}</TableCell>
      <TableCell>{item.No_Polisi}</TableCell>
      <TableCell>{item.Merek}</TableCell>
      <TableCell>{item.Tahun}</TableCell>
      <TableCell>{item.No_Rangka}</TableCell>
      <TableCell>{item.No_Mesin}</TableCell>
      <TableCell>{item.Kepemilikan}</TableCell>
    </TableRow>
  );

  const contentMaterial = (item) => (
    <TableRow key={item.id}>
      <TableCell>{item.No}</TableCell>
      <TableCell>{item.Nama_Dagang}</TableCell>
      <TableCell>{item.Cas_Number}</TableCell>
      <TableCell>{item.Karakteristik}</TableCell>
      <TableCell>{item.Fasa_B3}</TableCell>
      <TableCell>{item.Jenis_Kemasan}</TableCell>
      <TableCell>{item.Tujuan_Penggunaan}</TableCell>
    </TableRow>
  );

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "binary" });

          const vehicleSheet = XLSX.utils.sheet_to_json(workbook.Sheets["Data Kendaraan"], { header: 1 });
          if (vehicleSheet.length > 1) {
            const [...rows] = vehicleSheet;
            const vehicles = filterValidRows(rows, REQUIRED_FIELDS_VEHICLE);
            vehicles.shift();
            setVehicleData(vehicles);
            console.log(vehicles);
          }

          // Process Data B3
          const b3Sheet = XLSX.utils.sheet_to_json(workbook.Sheets["Data Bahan B3"], { header: 1 });
          if (b3Sheet.length > 1) {
            const [...rows] = b3Sheet;
            const substances = filterValidRows(rows, REQUIRED_FIELDS_B3);
            substances.shift();
            setB3Data(substances);
          }
        } catch (error) {
          toast.error("Failed to process the file. Please check the format.");
        } finally {
          setIsLoading(false);
          setIsUploaded(true); // Set upload success
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleCancel = () => {
    setVehicleData([]);
    setB3Data([]);
    setIsUploaded(false);
    setValue("file", null);
  };

  const processBahanB3Data = (b3Data) => {
    const processedData = [];

    b3Data.forEach((b3) => {
      const namaDagang = b3["Nama_Dagang"];
      const casNumberString = b3["Cas_Number"];

      // Split CAS number berdasarkan koma dan extract hanya angka CAS
      const casNumbers = casNumberString
        .split(",")
        .map((casEntry) => {
          // Extract CAS number dari format "Nama / CAS-Number"
          const casMatch = casEntry.trim().match(/(\d{2,7}-\d{2}-\d{1})/);
          return casMatch ? casMatch[1] : null;
        })
        .filter((cas) => cas !== null);

      // Duplikasi nama dagang untuk setiap CAS number
      casNumbers.forEach((casNumber) => {
        processedData.push({
          namaDagang: namaDagang,
          casNumber: casNumber,
          karakteristikB3: b3["Karakteristik"],
          fasaB3: b3["Fasa_B3"],
          jenisKemasan: b3["Jenis_Kemasan"],
          tujuanPenggunaan: b3["Tujuan_Penggunaan"],
        });
      });
    });

    return processedData;
  };

  async function handleOnSubmit(formData) {
    setFormErrorMessage(null);
    try {
      if (vehicleData.length === 0 || b3Data.length === 0) {
        toast.error("Semua data harus diisi sebelum mengajukan!");
        return;
      }

      const newPayload = {
        tipe: formData.tipe,
        companyId: formData.companyId,
        namaPenanggungJawab: formData.namaPenanggungJawab,
        jabatanPenanggungJawab: formData.jabatanPenanggungJawab,
        alamatDomisili: formData.alamatDomisili,
        nomorRegistrasi: formData.nomorRegistrasi,
        nomorSuratPerusahaan: formData.nomorSuratPerusahaan,
        nomorSuratRekomendasi: formData.nomorSuratRekomendasi ?? null,
        tanggalSuratPerusahaan: formData.tanggalSuratPerusahaan,
        tanggalSuratRekomendasi: formData.tanggalSuratRekomendasi ?? null,
        vehicles: vehicleData?.map((vehicle) => ({
          noPolisi: vehicle["No_Polisi"],
          modelKendaraan: vehicle["Merek"],
          tahunPembuatan: vehicle["Tahun"],
          nomorRangka: vehicle["No_Rangka"],
          nomorMesin: vehicle["No_Mesin"],
          kepemilikan: vehicle["Kepemilikan"],
        })),
        bahanb3: processBahanB3Data(b3Data),
        userId: isSuperAdmin ? null : user.id,
      };
      await postFetcher("/api/rekom/permohonan/create/from-excel", newPayload);

      toast.success("Data berhasil disiapkan untuk pengajuan!");
      //   redirect
      navigate("/rekomendasi-b3/daftar");
    } catch (error) {
      console.log(error);

      isResponseErrorObject(error.response.data);
    }
  }

  return (
    <RootAdmin>
      <div className="mt-3">
        <Card radius="sm">
          <CardBody>
            <div className="w-full flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Button onPress={() => navigate(-1)} isDisabled={isSubmitting} color="warning" variant="flat">
                  Kembali
                </Button>
                <a href={fileUrl} download="Draft Form Isian Permohonan.xlsx">
                  <Button startContent={<ArrowDownTrayIcon className="size-4" />} size="md" color="success">
                    Unduh Format File
                  </Button>
                </a>
              </div>
              <Divider orientation="vertical" className="h-10" />
              <div className="text-2xl font-semibold">Unggah dan Pratinjau Data Rekomendasi</div>
            </div>
          </CardBody>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Card radius="sm" className="col-span-3 md:col-span-1">
            <CardBody>
              <form onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-3">
                <ControlledInput control={control} name="namaPenanggungJawab" label="Nama Penanggung Jawab" isRequired={true} />
                <ControlledInput control={control} name="jabatanPenanggungJawab" label="Jabatan Penanggung Jawab" isRequired={true} />
                <ControlledInput control={control} name="alamatDomisili" label="Alamat Domisili" isRequired={true} />
                <ControlledReactSelect control={control} isLoading={isLoadingCompany} name="companyId" menuPosition="absolute" options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} label="Perusahaan" />
                <Select
                  {...register("tipe")}
                  variant="faded"
                  label="Tipe Surat"
                  placeholder="Pilih"
                  isRequired
                  labelPlacement="outside"
                  color={errors.tipe ? "danger" : "default"}
                  isInvalid={errors.tipe}
                  errorMessage={errors.tipe && errors.tipe.message}
                >
                  <SelectItem key={TipeSurat.BARU}>{TipeSurat.BARU}</SelectItem>
                  <SelectItem key={TipeSurat.PENAMBAHAN_JENIS_B3}>{TipeSurat.PENAMBAHAN_JENIS_B3}</SelectItem>
                  <SelectItem key={TipeSurat.PENAMBAHAN_KENDARAAN}>{TipeSurat.PENAMBAHAN_KENDARAAN}</SelectItem>
                  <SelectItem key={TipeSurat.PERPANJANGAN}>{TipeSurat.PERPANJANGAN}</SelectItem>
                </Select>
                <ControlledInput control={control} name="nomorRegistrasi" label="Nomor Registrasi" isRequired={true} />
                {type !== TipeSurat.BARU && (
                  <>
                    <ControlledInput control={control} name="nomorSuratRekomendasi" label="Nomor Surat Rekomendasi" isRequired={true} />
                    <ControlledInput control={control} type="date" name="tanggalSuratRekomendasi" label="Tanggal Surat Rekomendasi" isRequired={true} />
                  </>
                )}
                <ControlledInput control={control} name="nomorSuratPerusahaan" label="Nomor Surat Perusahaan" isRequired={true} />
                <ControlledInput control={control} type="date" name="tanggalSuratPerusahaan" label="Tanggal Surat Perusahaan" isRequired={true} />
                <div className="flex items-end gap-1">
                  <Input
                    {...register("file", { onChange: handleFileUpload })}
                    accept=".xlsx"
                    type="file"
                    label="File upload kendaraan B3"
                    labelPlacement="outside"
                    isRequired
                    variant="faded"
                    color={errors.file ? "danger" : "default"}
                    isInvalid={!!errors.file}
                    errorMessage={errors.file && errors.file?.message}
                  />
                  {isUploaded && (
                    <Button onClick={handleCancel} color="danger" isIconOnly>
                      <TrashIcon className="size-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-warning">Pastikan isi file sudah sesuai dengan file format yang disediakan</p>
                <div className="mt-4">
                  <Button type="submit" isLoading={isSubmitting} isDisabled={isSubmitting} color="primary">
                    Submit
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
          <Card radius="sm" className="col-span-3 md:col-span-2">
            <CardHeader></CardHeader>
            <CardBody className="flex flex-col gap-3">
              {formErrorMessage && (
                <ul className="text-xs text-white bg-danger p-3">
                  {Object.entries(formErrorMessage).map(([key, value]) => (
                    <li key={key}>{value}</li>
                  ))}
                </ul>
              )}
              {isLoading ? (
                <Spinner />
              ) : (
                <>
                  <Card radius="sm">
                    <CardHeader>Data Kendaraan</CardHeader>
                    <Divider />
                    <CardBody>
                      <ClientTablePagination header={formattedHeaderTableVehicle} content={contentVehicle} data={vehicleData} isLoading={isLoading} />
                    </CardBody>
                  </Card>
                  {/* Data B3 */}
                  <Card radius="sm">
                    <CardHeader>Data B3</CardHeader>
                    <Divider />
                    <CardBody>
                      <ClientTablePagination header={formattedHeaderTableMaterial} content={contentMaterial} data={b3Data} isLoading={isLoading} />
                    </CardBody>
                  </Card>
                </>
              )}
            </CardBody>
            <CardFooter></CardFooter>
          </Card>
        </div>
      </div>
    </RootAdmin>
  );
}
